import { platformOpsApi } from ".";

export interface RestoreCustomerResponse {
  id: number;
  createdBy: string | null;
  customerId: number;
  databaseLetter: string;
  companyName: string;
  sourceDatabase: string;
  targetDatabase: string;
  server: string;
  dateAdded: string;
  statusId: number;
  statusName: string;
  isActive: boolean;
}

export interface CreateRestoreCustomerRequest {
  customerId: number;
  databaseLetter: string;
  companyName: string;
  sourceDatabase: string;
  targetDatabase: string;
  server: string;
  statusId?: number;
  createdBy: string;
}

export interface RestoreJobStepResponse {
  id: number;
  stepTypeName: string;
  sortOrder: number;
  status: string;
  startedOn: string | null;
  completedOn: string | null;
  errorMessage: string | null;
}

export interface RestoreJobResponse {
  id: number;
  customerId: number;
  requestedBy: string | null;
  requestedAt: string;
  startedOn: string | null;
  status: string;
  completedOn: string | null;
  steps: RestoreJobStepResponse[];
}

export interface CreateRestoreJobRequest {
  customerId: number;
  requestedBy: string;
  stepTypeIds?: number[];
}

export interface ProvisionCustomerRequest {
  name: string;
  description?: string;
  customerNumber?: number;
  databaseIdentifier?: string;
  customerTypeId?: number;
  environment?: string;
}

export interface ProvisionCustomerResponse {
  id: number;
  name: string;
  description: string | null;
  customerNumber: number | null;
  databaseIdentifier: string | null;
  customerTypeId: number | null;
  environment: string | null;
  isActive: boolean;
}

export interface DatabaseResponse {
  companyId: number;
  companyLetter: string;
  companyName: string;
  databaseName: string;
}

export interface CreateDatabaseOnServerRequest {
  companyId: number;
  companyLetter: string;
  companyName: string;
  databaseName: string;
}

export interface XpressTraxCustomerResponse {
  companyId: number;
  companyName: string;
  serverId: string | null;
  isActive: boolean;
}

export interface XpressTraxCustomersResponse {
  customers: XpressTraxCustomerResponse[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface RestoreJobStepTypeResponse {
  id: number;
  name: string;
  sortOrder: number;
  category: string;
}

const xpressTraxUrl = "/v1/xpresstrax";
const restoreCustomersUrl = "/v1/restorecustomers";
const restoreJobsUrl = "/v1/databases/restore/jobs";
const restoreStepTypesUrl = "/v1/databases/restore/step-types";
const databasesUrl = "/v1/explicense";
const authorizationUrl = "/v1/authorization";

export const searchXpressTraxCustomers = async (searchTerm: string): Promise<XpressTraxCustomersResponse> => {
  const isNumeric = /^\d+$/.test(searchTerm);
  const params = isNumeric
    ? { companyIds: Number(searchTerm), pageSize: 50 }
    : { companyName: searchTerm, pageSize: 50 };
  const response = await platformOpsApi.get<XpressTraxCustomersResponse>(
    `${xpressTraxUrl}/customers`,
    { params }
  );
  return response.data;
};

export const getRestoreCustomers = async (): Promise<RestoreCustomerResponse[]> => {
  const response = await platformOpsApi.get<RestoreCustomerResponse[]>(restoreCustomersUrl);
  return response.data;
};

export const createRestoreCustomer = async (request: CreateRestoreCustomerRequest): Promise<RestoreCustomerResponse> => {
  const response = await platformOpsApi.post<RestoreCustomerResponse>(restoreCustomersUrl, request);
  return response.data;
};

export const deleteRestoreCustomer = async (id: number): Promise<void> => {
  await platformOpsApi.delete(`${restoreCustomersUrl}/${id}`);
};

export const updateRestoreCustomerStatus = async (id: number, statusId: number): Promise<RestoreCustomerResponse> => {
  const response = await platformOpsApi.patch<RestoreCustomerResponse>(`${restoreCustomersUrl}/${id}`, { statusId });
  return response.data;
};

export const getRestoreJobs = async (): Promise<RestoreJobResponse[]> => {
  const response = await platformOpsApi.get<RestoreJobResponse[]>(restoreJobsUrl);
  return response.data;
};

export const getRestoreJobStepTypes = async (): Promise<RestoreJobStepTypeResponse[]> => {
  const response = await platformOpsApi.get<RestoreJobStepTypeResponse[]>(restoreStepTypesUrl);
  return response.data;
};

export const createRestoreJob = async (request: CreateRestoreJobRequest): Promise<RestoreJobResponse> => {
  const response = await platformOpsApi.post<RestoreJobResponse>(restoreJobsUrl, request);
  return response.data;
};

export const updateRestoreJobStep = async (
  jobId: number,
  stepId: number,
  statusId: number,
  errorMessage?: string
): Promise<RestoreJobStepResponse> => {
  const response = await platformOpsApi.patch<RestoreJobStepResponse>(
    `${restoreJobsUrl}/${jobId}/steps/${stepId}`,
    { statusId, errorMessage }
  );
  return response.data;
};

export const provisionAuthCustomer = async (request: ProvisionCustomerRequest): Promise<ProvisionCustomerResponse> => {
  const response = await platformOpsApi.post<ProvisionCustomerResponse>(`${authorizationUrl}/customers`, request);
  return response.data;
};

export const getDatabases = async (companyId: number, server: string): Promise<DatabaseResponse[]> => {
  const response = await platformOpsApi.get<DatabaseResponse[]>(`${databasesUrl}/server/${server}/company/${companyId}`);
  return response.data;
};

export const createDatabaseOnServer = async (
  serverId: string,
  request: CreateDatabaseOnServerRequest
): Promise<DatabaseResponse> => {
  const response = await platformOpsApi.post<DatabaseResponse>(`${databasesUrl}/server/${serverId}`, request);
  return response.data;
};
